import React, {createRef, useRef} from 'react';
import PropTypes from 'prop-types';
import {Input, ListGroupItem} from "reactstrap";
import Downshift from "downshift";
import classnames from "classnames";
import useAfterUpdateEffect from "../utils/useAfterUpdateEffect";
import useDidMountEffect from "../utils/useDidMountEffect";
import {Portal} from "react-portal";

const InputAutocomplete = props => {
  const ListBoxComponent = props.portal ? Portal : React.Fragment;
  const inputAutocompleteRef = useRef();
  const [listBoxStyle, setListBoxStyle] = React.useState({});
  const [inputValue, setInputValue] = React.useState(props.value);
  const [selection, setSelection] = React.useState(props?.selection ?? null);
  const [apiListParams, setApiListParams] = React.useState(props.apiListParams);
  const [items, setItems] = React.useState([]);

  useDidMountEffect(() => {
    computeListBoxStyles();
    fetchList()
  })

  useAfterUpdateEffect(() => {
    handleInputValueChange(props.value)
  }, [props.value])

  useAfterUpdateEffect(() => {
    props.onChange && props.onChange({
      value: inputValue,
      selection,
    })
  }, [selection, inputValue])

  React.useEffect(() => {
    fetchList()
  }, [apiListParams]);

  React.useEffect(() => {
    if(JSON.stringify(props.apiListParams) !== JSON.stringify(apiListParams)){
      setApiListParams(props.apiListParams)
    }
  }, [props.apiListParams]);

  const computeListBoxStyles = () => {
    setListBoxStyle({
      top: computeTopWith(inputAutocompleteRef),
      left: computeLeftWith(inputAutocompleteRef),
    })
  }

  React.useEffect(() => {
    const appBody = document.querySelector('#root > .app > .app-body');
    appBody.addEventListener("scroll", computeListBoxStyles);
    return () => appBody.removeEventListener("scroll", computeListBoxStyles);
  }, [])

  const fetchList = () => {
    setItems([])

    if(props.apiListFn){
      props.apiListFn(props.apiListParams)
        .then(res => {
          setItems(res.data['hydra:member'])
        })
    }
  }

  const handlerInputOnBlur = () => {
    props.onAccept && props.onAccept({
      value: inputValue,
      selection,
    })
  }

  const getItems = (inputValue) => {
    return items.filter(item => item.name?.toLowerCase()?.includes(inputValue?.toLowerCase()))
  }

  const handleInputValueChange = (inputValue) => {
    const selectionByInputValue = items.find(item => item.name?.toLowerCase() === inputValue?.toLowerCase())
    setSelection(selectionByInputValue ?? null)
    setInputValue(selectionByInputValue?.name ?? inputValue)
  }

  const getListBoxStyle = () => {
    if(props.portal){
      return listBoxStyle
    }

    return {};
  }

  const computeTopWith = (ref) => {
    return (ref.current.getBoundingClientRect().top + ref.current.getBoundingClientRect().height) + 'px'
  }

  const computeLeftWith = (ref) => {
    return ref.current.getBoundingClientRect().left + 'px'
  }

  return (
    <Downshift
      onStateChange={(state) => {
        if(state.type === Downshift.stateChangeTypes.changeInput) {
          handleInputValueChange(state?.inputValue)
        }
      }}
      inputValue={inputValue}
      onChange={selection => {
        setSelection(selection)
        setInputValue(selection.name)

        props.onAccept && props.onAccept({
          value: selection.name,
          selection,
        })

      }}
      selectedItem={selection}
      itemToString={item => item?.name ?? ''}
    >
      {({
          getInputProps,
          getItemProps,
          getMenuProps,
          inputValue,
          isOpen,
          highlightedIndex,
          selectedItem,
          getRootProps,
          openMenu,
        }) => (
        <div ref={inputAutocompleteRef} className="input-autocomplete" style={{position: 'relative'}}>
          <div
            style={{}}
            {...getRootProps({}, { suppressRefError: true })}
          >
            <Input
              {...getInputProps()}
              autoFocus={props.autoFocus}
              className="form-control"
              placeholder={props.placeholder}
              onFocus={() => openMenu()}
              onBlur={handlerInputOnBlur}
            />
          </div>
          {isOpen && <ListBoxComponent>
            <div {...getMenuProps()} className="input-autocomplete-list-box" style={getListBoxStyle()}>
              {getItems(inputValue).map((item, index) => (
                <ListGroupItem
                  className={classnames({
                    'p-1': true,
                    'font-weight-bold bg-primary': selectedItem === item,
                    'bg-secondary': highlightedIndex === index,
                  })}
                  {...getItemProps({
                    key: index.toString(),
                    index,
                    item,
                    style: {
                      backgroundColor:
                        highlightedIndex === index
                          ? 'lightgray'
                          : 'white',
                      fontWeight:
                        selectedItem === item ? 'bold' : 'normal',
                    },
                  })}
                >
                  {item.name}
                </ListGroupItem>
              ))}
            </div>
          </ListBoxComponent>}
        </div>
      )}
    </Downshift>
  );
};

InputAutocomplete.defaultProps = {
  apiListParams: {},
  value: '',
  placeholder: '',
  autoFocus: false,
  portal: false,
};

InputAutocomplete.propTypes = {
  portal: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onChange: PropTypes.func,
  onAccept: PropTypes.func,
  placeholder: PropTypes.string,
  apiListFn: PropTypes.func,
  apiListParams: PropTypes.object,
  value: PropTypes.string,
};

export default InputAutocomplete;
